// Organization Health Summary Component
// Displays AI-driven health metrics from the Compliance Officer

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { 
  Shield, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Clock,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format currency
const formatCurrency = (value) => {
  if (!value || value === 0) return '$0';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

// Health score color
const getHealthColor = (score) => {
  if (score >= 75) return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (score >= 50) return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
  return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
};

export function OrganizationHealthSummary({ organizationId, authToken, compact = false }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealthSummary = useCallback(async () => {
    if (!organizationId || !authToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/health-summary/${organizationId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch health summary');
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching health summary:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, authToken]);

  useEffect(() => {
    fetchHealthSummary();
  }, [fetchHealthSummary]);

  if (isLoading) {
    return (
      <Card className="border-slate-200" data-testid="health-summary-loading">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
          <span className="ml-2 text-slate-500">Loading health metrics...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="health-summary-error">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">Failed to load health metrics</span>
            <Button variant="ghost" size="sm" onClick={fetchHealthSummary}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const healthColor = getHealthColor(data.health_score);
  const { metrics } = data;

  // Compact version for sidebar or cards
  if (compact) {
    return (
      <Card className="border-slate-200" data-testid="health-summary-compact">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${healthColor.bg}`}>
                <Activity className={`w-4 h-4 ${healthColor.text}`} />
              </div>
              <span className="font-semibold text-slate-800">Health Score</span>
            </div>
            <span className={`text-2xl font-bold ${healthColor.text}`}>{data.health_score}%</span>
          </div>
          <Progress value={data.health_score} className="h-2" />
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
            <span>{metrics.active_pursuits} active pursuits</span>
            <span>{metrics.high_risk} high risk</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full version
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="health-summary-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            Portfolio Health
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchHealthSummary} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Health Score Hero */}
        <div className={`rounded-xl p-4 ${healthColor.bg} ${healthColor.border} border`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${healthColor.text}`}>Overall Health</span>
            <span className={`text-3xl font-bold ${healthColor.text}`}>{data.health_score}%</span>
          </div>
          <Progress value={data.health_score} className="h-3" />
          <p className="text-xs mt-2 text-slate-600">
            Based on {metrics.active_pursuits} active pursuits, avg pWin {metrics.avg_pwin}%
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold text-slate-900">{metrics.total_pursuits}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <p className="text-2xl font-bold text-emerald-700">{metrics.won}</p>
            <p className="text-xs text-emerald-600">Won</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-700">{metrics.lost}</p>
            <p className="text-xs text-red-600">Lost</p>
          </div>
        </div>

        {/* Risk Breakdown */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-2">Risk Distribution</h4>
          <div className="flex gap-2">
            <Badge className="bg-red-100 text-red-700 border-red-200">
              {metrics.high_risk} High
            </Badge>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
              {metrics.medium_risk} Medium
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              {metrics.low_risk} Low
            </Badge>
          </div>
        </div>

        {/* Pipeline Value */}
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-slate-700">Pipeline Value</span>
          </div>
          <span className="text-xl font-bold text-emerald-700">
            {formatCurrency(metrics.pipeline_value)}
          </span>
        </div>

        {/* Upcoming Deadlines */}
        {data.upcoming_deadlines?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Upcoming Deadlines
            </h4>
            <div className="space-y-2">
              {data.upcoming_deadlines.slice(0, 3).map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"
                >
                  <span className="text-slate-700 truncate max-w-[180px]">{item.name}</span>
                  <div className="flex items-center gap-2">
                    {item.risk === 'HIGH' && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                    <span className="text-slate-500">
                      {new Date(item.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OrganizationHealthSummary;
